/**
 * The pregnancy follow-up, as the server keeps it.
 *
 * This replaces a browser-local prototype. Whether someone is following a
 * pregnancy, what her care dates it from, and how far along she is were held in
 * `AsyncStorage` — which meant the answer changed with the device, could not be
 * read by anyone else looking after her, and was exactly the pattern this repo
 * forbids for real data. The contract the prototype was waiting for exists:
 * `/cycles/{cycleId}/pregnancy-follow-up` stores the opt-in and the dating
 * anchor, and returns the gestation computed from it.
 *
 * The server owns the arithmetic that matters clinically — `gestationalWeeks`,
 * `estimatedDueDate`, and which anchor they came from — so nothing here
 * recomputes them. `gestationalDates.ts` only turns the server's answer into
 * calendar days for a milestone written in weeks.
 *
 * `gestation` is null whenever the pregnancy cannot be dated. That is not an
 * error to paper over: without an anchor there is no week count, and the app must
 * say nothing rather than infer one.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import { cycleMutationInvalidationKeys } from "@src/packages/cycles/cycle-cache-keys";
import type { paths } from "@src/packages/openapi/schema";

declare const __DEV__: boolean | undefined;

type FollowUpPath = paths["/cycles/{cycleId}/pregnancy-follow-up"];

export type CyclePregnancyFollowUp =
  FollowUpPath["get"]["responses"][200]["content"]["application/json"]["data"];

export type CyclePregnancyFollowUpWrite = NonNullable<
  FollowUpPath["put"]["requestBody"]
>["content"]["application/json"];

/** The gestation block, present only once the pregnancy can be dated. */
export type CyclePregnancyGestation = NonNullable<
  CyclePregnancyFollowUp["gestation"]
>;

/**
 * The follow-up a `?dev_pregnancy_tracking=` fixture stands for.
 *
 * The visual-evidence suite drives two routes off this parameter, and it fed the
 * browser-local prototype that the server contract replaced — so migrating left
 * both routes rendering the same screen and quietly stopped proving anything.
 * This restores them against the shape the API actually returns.
 *
 * Development only, like every other fixture here: `__DEV__` is false in any
 * build a patient can reach, and no fixture value is ever written back.
 */
function readDevFollowUpStatus(): CyclePregnancyFollowUp["status"] | null {
  if (typeof __DEV__ === "undefined" || !__DEV__) return null;
  if (typeof window === "undefined") return null;
  try {
    const value = new URLSearchParams(window.location.search).get(
      "dev_pregnancy_tracking",
    );
    if (value === "opted-in") return "opted_in";
    if (value === "not-configured") return "not_configured";
    return null;
  } catch {
    return null;
  }
}

const DAY_MS = 24 * 60 * 60 * 1000;
/** Full term, for the estimated due date a real gestation would carry. */
const TERM_DAYS = 280;

/**
 * A follow-up the fixture routes can render, dated from the day it is viewed.
 *
 * Six weeks and two days: far enough in that the first-scan window is ahead of
 * her and the repeat beta behind, so a capture shows both a coming and a passed
 * milestone rather than a list in one state.
 */
function buildDevFollowUp(
  cycleId: string,
  status: CyclePregnancyFollowUp["status"],
): CyclePregnancyFollowUp {
  const gestationalDays = 6 * 7 + 2;
  const today = new Date();
  const asOf = new Date(
    Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()),
  );
  const dueDate = new Date(
    asOf.getTime() + (TERM_DAYS - gestationalDays) * DAY_MS,
  );
  const anchor = new Date(asOf.getTime() - gestationalDays * DAY_MS);

  return {
    cycleId,
    outcomeId: null,
    status,
    datingMethod: "transfer",
    datingAnchorDate: anchor.toISOString().slice(0, 10),
    datingAnchorPrecision: "day",
    transferEmbryoDay: 5,
    optedInAt: status === "opted_in" ? asOf.toISOString() : null,
    endedAt: null,
    endedReason: null,
    gestation:
      status === "opted_in"
        ? {
            asOf: asOf.toISOString().slice(0, 10),
            gestationalDays,
            gestationalWeeks: 6,
            gestationalDayOfWeek: 2,
            estimatedDueDate: dueDate.toISOString().slice(0, 10),
            method: "transfer",
            basis: "transfer_date",
          }
        : null,
  };
}

export const pregnancyFollowUpKeys = {
  all: ["cycle-pregnancy-follow-up"] as const,
  cycle: (cycleId: string) => ["cycle-pregnancy-follow-up", cycleId] as const,
};

/**
 * Reads the follow-up for a cycle.
 *
 * Disabled without a cycle rather than fetching a guess: this record is scoped
 * to one cycle's outcome, and a follow-up is never shared across outcomes — a
 * corrected or later result starts again at `not_configured`.
 */
export function useCyclePregnancyFollowUp(cycleId?: string | null) {
  const { client } = useApiClient();

  return useQuery({
    queryKey: pregnancyFollowUpKeys.cycle(cycleId ?? ""),
    queryFn: async (): Promise<CyclePregnancyFollowUp | null> => {
      if (!cycleId) return null;
      const devStatus = readDevFollowUpStatus();
      if (devStatus) return buildDevFollowUp(cycleId, devStatus);
      try {
        const { data } = await client.GET(
          "/cycles/{cycleId}/pregnancy-follow-up",
          { params: { path: { cycleId } } },
        );
        return data?.data ?? null;
      } catch (error) {
        throwApiError(
          error,
          "Failed to load the pregnancy follow-up",
          "cycle-pregnancy-follow-up",
        );
      }
    },
    enabled: Boolean(cycleId),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Writes the follow-up: the opt-in, the dismissal, the end, and the dating.
 *
 * Invalidates the cycle family as well as its own key. The dating anchor changes
 * what the gestation is, and the gestation is read beside cycle-derived state;
 * refreshing one without the other is how two surfaces come to disagree about how
 * far along someone is.
 */
export function useUpdateCyclePregnancyFollowUp() {
  const { client } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      body,
    }: {
      cycleId: string;
      body: CyclePregnancyFollowUpWrite;
    }): Promise<CyclePregnancyFollowUp> => {
      const { data, error } = await client.PUT(
        "/cycles/{cycleId}/pregnancy-follow-up",
        { params: { path: { cycleId } }, body },
      );

      if (data) return data.data;
      const message =
        typeof error === "string"
          ? error
          : error && typeof error === "object" && "message" in error
            ? String(error.message)
            : "Failed to save the pregnancy follow-up";
      throw new Error(message);
    },
    onSuccess: (_result, { cycleId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: pregnancyFollowUpKeys.cycle(cycleId),
        }),
        ...cycleMutationInvalidationKeys().map((key) =>
          queryClient.invalidateQueries({ queryKey: key }),
        ),
      ]),
  });
}
